import { debounce } from "lodash";
import { useQueryParams } from "PFCore/helpers/use_query_params";
import { RefObject } from "react";
import { useHistory } from "react-router-dom";

const DEBOUNCE_TIME = 1500;

type UseSearchInput = {
  searchInputRef: RefObject<HTMLInputElement>;
  onTermChange: (term: string) => void;
  debounceTime?: number;
  withUrlChanges?: boolean;
};

export const useSearchInput = ({
  searchInputRef,
  onTermChange,
  debounceTime = DEBOUNCE_TIME,
  withUrlChanges = true
}: UseSearchInput) => {
  const history = useHistory();
  const searchParams = useQueryParams();

  const updateUrl = (term: string) => {
    if (!withUrlChanges) {
      return;
    }
    term ? searchParams.set("term", term) : searchParams.delete("term");
    history.replace({ search: searchParams.toString() });
  };

  const handleDebounceChange = debounce(() => {
    if (!searchInputRef || !searchInputRef.current) {
      return;
    }

    const { value } = searchInputRef.current;

    updateUrl(value);
    onTermChange(value);
  }, debounceTime);

  const handleClear = () => {
    if (!searchInputRef || !searchInputRef.current) {
      return;
    }

    searchInputRef.current.value = "";
    setTimeout(() => searchInputRef?.current?.focus(), 200);
    onTermChange("");
    updateUrl("");
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchInputRef || !searchInputRef.current) {
      return;
    }

    const { value } = searchInputRef.current;

    if (["Enter", "Escape"].includes(event.key)) {
      updateUrl(value);
      onTermChange(value);
    }
  };

  return {
    handleSearchKeyDown,
    handleClear,
    handleDebounceChange
  };
};
